"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function EfficiencyMetrics() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gc-intro-el", {
        y: 24
      });
      gsap.to(".gc-intro-el", {
        opacity: 1,
        y: 0,
        duration: 0.72,
        stagger: 0.09,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      const num1 = el.querySelector<HTMLElement>(".gc-num-1");
      const num2 = el.querySelector<HTMLElement>(".gc-num-2");
      const num3 = el.querySelector<HTMLElement>(".gc-num-3");
      if (num1) {
        const obj = {
          val: 0
        };
        gsap.to(obj, {
          val: 87,
          duration: 1.4,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 75%",
            once: true
          },
          onUpdate: () => {
            num1.textContent = Math.round(obj.val).toString();
          }
        });
      }
      if (num2) {
        const obj = {
          val: 10
        };
        gsap.to(obj, {
          val: 2,
          duration: 1.4,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 75%",
            once: true
          },
          onUpdate: () => {
            num2.textContent = "<" + obj.val.toFixed(1) + "s";
          }
        });
      }
      if (num3) {
        const obj = {
          val: 0
        };
        gsap.to(obj, {
          val: 99,
          duration: 1.4,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 75%",
            once: true
          },
          onUpdate: () => {
            num3.textContent = Math.round(obj.val).toString() + "%";
          }
        });
      }
      gsap.set(".gc-stat", {
        y: 40
      });
      gsap.to(".gc-stat", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 78%",
          once: true
        }
      });
      gsap.to(".gc-caption", {
        opacity: 1,
        duration: 0.7,
        stagger: 0.1,
        delay: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 78%",
          once: true
        }
      });
      gsap.to(".gc-hairline", {
        opacity: 0.4,
        duration: 0.8,
        delay: 0.3,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 78%",
          once: true
        }
      });
      gsap.to(".gc-aurora", {
        rotation: 360,
        duration: 40,
        repeat: -1,
        ease: "none",
        transformOrigin: "50% 50%"
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".gc-intro-el", ".gc-stat"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".gc-caption", {
        opacity: 1
      });
      gsap.set(".gc-hairline", {
        opacity: 0.4
      });
      const num1 = el.querySelector<HTMLElement>(".gc-num-1");
      const num2 = el.querySelector<HTMLElement>(".gc-num-2");
      const num3 = el.querySelector<HTMLElement>(".gc-num-3");
      if (num1) num1.textContent = "87";
      if (num2) num2.textContent = "<2.0s";
      if (num3) num3.textContent = "99%";
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31351" id="efficiencymetrics" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-32 md:py-40 lg:py-48">
      <div ref={container} className="relative mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-14">
        {}
        <div aria-hidden="true" className="gc-aurora pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[1200px] w-[1200px] -translate-x-1/2 -translate-y-1/2" style={{
        background: "radial-gradient(circle, hsl(215 90% 45% / 0.08) 0%, hsl(215 90% 45% / 0.04) 30%, hsl(215 80% 20% / 0.02) 55%, transparent 72%)"
      }} />

        {}
        <div className="relative z-10 mx-auto max-w-[720px] text-center py-12 md:py-16">
          <div className="gc-intro-el flex items-center justify-center gap-3" style={{
          opacity: 0
        }}>
            <span aria-hidden="true" className="inline-block h-px w-6 bg-primary" />
            <span className="font-default text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Excelência Técnica
            </span>
          </div>

          <h2 className="gc-intro-el font-default mt-6 text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tighter text-foreground" style={{
          opacity: 0,
          lineHeight: 1.06
        }}>
            Engenharia de precisão.
          </h2>

          <p className="gc-intro-el font-default mt-6 text-base md:text-lg text-muted-foreground" style={{
          opacity: 0,
          lineHeight: 1.6
        }}>
            Soluções avançadas em sistemas de pulverização industrial, garantindo eficiência e qualidade desde 1937.
          </p>
        </div>

        {}
        <div className="relative z-10 mt-16 md:mt-24">
          <div className="grid grid-cols-1 md:grid-cols-3 relative">
            {}
            <div aria-hidden="true" className="gc-hairline hidden md:block absolute left-1/3 top-1/2 h-[120px] w-px bg-border -translate-y-1/2" style={{
            opacity: 0
          }} />
            <div aria-hidden="true" className="gc-hairline hidden md:block absolute left-2/3 top-1/2 h-[120px] w-px bg-border -translate-y-1/2" style={{
            opacity: 0
          }} />

            {}
            <div className="gc-stat flex flex-col items-center justify-center text-center px-4 py-12 md:py-8" style={{
            opacity: 0
          }} data-index={0}>
              <div className="flex items-baseline justify-center flex-wrap">
                <span className="gc-num-1 font-default text-primary" style={{
                fontSize: "clamp(4.5rem, 10vw, 10rem)",
                fontWeight: 700,
                letterSpacing: "-0.04em",
                lineHeight: 0.95
              }}>
                  0
                </span>
                <span className="font-default ml-2 md:ml-3 text-muted-foreground" style={{
                fontSize: "clamp(1rem, 1.4vw, 1.375rem)",
                fontWeight: 400,
                letterSpacing: "-0.01em"
              }}>
                  projetos
                </span>
              </div>
              <div className="gc-caption font-default mt-6 text-xs font-semibold uppercase tracking-widest text-muted-foreground" style={{
              opacity: 0
            }}>
                Sistemas Instalados
              </div>

              {}
              <div aria-hidden="true" className="gc-hairline md:hidden mt-12 h-px w-24 bg-border" style={{
              opacity: 0
            }} />
            </div>

            {}
            <div className="gc-stat flex flex-col items-center justify-center text-center px-4 py-12 md:py-8" style={{
            opacity: 0
          }} data-index={1}>
              <div className="flex items-baseline justify-center flex-wrap">
                <span className="gc-num-2 font-default text-secondary" style={{
                fontSize: "clamp(4.5rem, 10vw, 10rem)",
                fontWeight: 700,
                letterSpacing: "-0.04em",
                lineHeight: 0.95
              }}>
                  {"<2.0s"}
                </span>
                <span className="font-default ml-2 md:ml-3 text-muted-foreground" style={{
                fontSize: "clamp(1rem, 1.4vw, 1.375rem)",
                fontWeight: 400,
                letterSpacing: "-0.01em"
              }}>
                  resposta
                </span>
              </div>
              <div className="gc-caption font-default mt-6 text-xs font-semibold uppercase tracking-widest text-muted-foreground" style={{
              opacity: 0
            }}>
                Suporte Técnico Ágil
              </div>

              <div aria-hidden="true" className="gc-hairline md:hidden mt-12 h-px w-24 bg-border" style={{
              opacity: 0
            }} />
            </div>

            {}
            <div className="gc-stat flex flex-col items-center justify-center text-center px-4 py-12 md:py-8" style={{
            opacity: 0
          }} data-index={2}>
              <div className="flex items-baseline justify-center flex-wrap">
                <span className="gc-num-3 font-default text-primary" style={{
                fontSize: "clamp(4.5rem, 10vw, 10rem)",
                fontWeight: 700,
                letterSpacing: "-0.04em",
                lineHeight: 0.95
              }}>
                  0%
                </span>
                <span className="font-default ml-2 md:ml-3 text-muted-foreground" style={{
                fontSize: "clamp(1rem, 1.4vw, 1.375rem)",
                fontWeight: 400,
                letterSpacing: "-0.01em"
              }}>
                  satisfação
                </span>
              </div>
              <div className="gc-caption font-default mt-6 text-xs font-semibold uppercase tracking-widest text-muted-foreground" style={{
              opacity: 0
            }}>
                Qualidade Garantida
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}